import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Anchor, Mic, MicOff, Send, X, Clock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ExamSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [questionNum, setQuestionNum] = useState(1);
  const [functionName, setFunctionName] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [typingIndicator, setTypingIndicator] = useState(false);

  // Voice / Whisper state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micSupported, setMicSupported] = useState(false);
  const [recDuration, setRecDuration] = useState(0);

  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recDurationRef = useRef(0);

  // ── Load session ──
  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}`);
      setFunctionName(data.function_name || "");
      setSessionComplete(!data.is_active);
      const msgs = (data.messages || []).map((m) => ({
        id: m.id, role: m.role, text: m.message_text,
      }));
      setMessages(msgs);
      const qNums = (data.messages || []).filter(m => m.question_number).map(m => m.question_number);
      if (qNums.length) setQuestionNum(Math.max(...qNums));
    } catch {
      toast.error("Failed to load session");
      navigate("/dashboard");
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    const initialMsg = location.state?.initialMessage;
    const fname = location.state?.functionName;
    if (initialMsg) {
      setMessages([{ id: "init", role: "surveyor", text: initialMsg }]);
      if (fname) setFunctionName(fname);
    } else {
      loadSession();
    }
  }, [location.state, loadSession]);

  // ── Session timer ──
  useEffect(() => {
    timerRef.current = setInterval(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // ── Auto-scroll ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingIndicator]);

  // ── Detect microphone support (MediaRecorder) ──
  useEffect(() => {
    setMicSupported(
      typeof navigator.mediaDevices?.getUserMedia === "function" &&
      typeof window.MediaRecorder === "function"
    );
  }, []);

  // ── Recording helpers ──
  const stopRecordingCleanup = useCallback(() => {
    clearInterval(recTimerRef.current);
    recTimerRef.current = null;
    recDurationRef.current = 0;
    setRecDuration(0);
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pick a supported mime type
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Stop all mic tracks so the browser indicator disappears
        stream.getTracks().forEach((t) => t.stop());
        stopRecordingCleanup();

        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });

        if (blob.size < 100) {
          toast.error("Recording was too short. Please try again.");
          return;
        }

        // Upload to backend for Whisper transcription
        setIsTranscribing(true);
        try {
          const ext = (recorder.mimeType || "audio/webm").includes("webm") ? "webm" : "mp4";
          const formData = new FormData();
          formData.append("file", blob, `answer.${ext}`);

          const { data } = await api.post("/transcribe", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });

          if (data.text) {
            setInput((prev) => {
              const combined = prev ? `${prev} ${data.text}` : data.text;
              return combined.substring(0, 500);
            });
          }
        } catch (err) {
          const msg =
            err.response?.data?.detail ||
            "Voice transcription failed. Please type your answer instead.";
          toast.error(msg);
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.onerror = () => {
        stream.getTracks().forEach((t) => t.stop());
        stopRecordingCleanup();
        toast.error("Recording error. Please try again or type your answer.");
      };

      mediaRecorderRef.current = recorder;
      recorder.start(1000); // collect data every second
      setIsRecording(true);
      recDurationRef.current = 0;
      setRecDuration(0);

      // Duration timer with 120s cap
      recTimerRef.current = setInterval(() => {
        recDurationRef.current += 1;
        setRecDuration(recDurationRef.current);
        if (recDurationRef.current >= 120) {
          toast.info("Recording limit reached (2 min). Review your answer and tap Send.");
          recorder.stop();
        }
      }, 1000);
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Microphone permission denied. Please allow access in your browser settings.");
      } else {
        toast.error("Could not access microphone. Please type your answer instead.");
      }
    }
  }, [stopRecordingCleanup]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const toggleMic = () => {
    if (isTranscribing) return; // wait for transcription to finish
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // ── Send message ──
  const handleSend = async () => {
    const text = input.trim().substring(0, 500);
    if (!text || sending || cooldown || sessionComplete) return;

    // Stop recording if active
    if (isRecording) stopRecording();

    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { id: `u_${Date.now()}`, role: "candidate", text }]);
    setTypingIndicator(true);

    setCooldown(true);
    setCooldownSec(3);
    const cdInterval = setInterval(() => {
      setCooldownSec((s) => {
        if (s <= 1) { clearInterval(cdInterval); setCooldown(false); return 0; }
        return s - 1;
      });
    }, 1000);

    try {
      const { data } = await api.post(`/sessions/${sessionId}/message`, { message: text });
      setTypingIndicator(false);
      setMessages((prev) => [...prev, { id: `ai_${Date.now()}`, role: "surveyor", text: data.ai_message }]);
      if (data.question_number) setQuestionNum(data.question_number);
      if (data.session_complete) {
        setSessionComplete(true);
        clearInterval(timerRef.current);
      }
    } catch (err) {
      setTypingIndicator(false);
      const msg = err.response?.data?.detail || "Failed to send message";
      toast.error(msg);
      if (err.response?.status === 400 && msg.includes("ended")) {
        setSessionComplete(true);
      }
    } finally {
      setSending(false);
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm("Are you sure you want to end this session early?")) return;
    try {
      await api.post(`/sessions/${sessionId}/end`);
      toast.info("Session ended");
      navigate(`/session/${sessionId}/results`);
    } catch {
      toast.error("Failed to end session");
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Determine mic button visual state
  const micBusy = isRecording || isTranscribing;

  return (
    <div className="flex flex-col h-screen h-[100dvh] bg-slate-50">
      {/* Top Bar */}
      <header className="cop-glass border-b px-4 py-2 flex items-center gap-3 z-30 flex-shrink-0" data-testid="session-header">
        <Button variant="ghost" size="sm" className="p-1" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Anchor className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate" data-testid="function-name">{functionName}</p>
          <p className="text-xs text-muted-foreground" data-testid="question-counter">Question {questionNum} of 10</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="session-timer">
            <Clock className="h-3 w-3" /> {formatTime(elapsedTime)}
          </span>
          {!sessionComplete && (
            <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={handleEndSession} data-testid="end-session-btn">
              <X className="h-3 w-3 mr-1" /> End
            </Button>
          )}
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`cop-fade-in ${msg.role === "candidate" ? "flex justify-end" : "flex justify-start"}`}>
            <div className={msg.role === "candidate" ? "cop-bubble-user p-3 shadow-sm" : "cop-bubble-ai p-3 shadow-sm"} data-testid={`chat-bubble-${msg.role}`}>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
            </div>
          </div>
        ))}
        {typingIndicator && (
          <div className="flex justify-start cop-fade-in">
            <div className="cop-bubble-ai p-3 flex items-center gap-1.5" data-testid="typing-indicator">
              <div className="w-2 h-2 bg-muted-foreground/50 rounded-full cop-typing-dot" />
              <div className="w-2 h-2 bg-muted-foreground/50 rounded-full cop-typing-dot" />
              <div className="w-2 h-2 bg-muted-foreground/50 rounded-full cop-typing-dot" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t bg-white px-4 py-3 flex-shrink-0" data-testid="chat-input-area">
        {sessionComplete ? (
          <div className="text-center py-2">
            <p className="text-sm font-medium text-muted-foreground mb-3" data-testid="session-complete-msg">Session Complete</p>
            <Button size="sm" onClick={() => navigate(`/session/${sessionId}/results`)} data-testid="view-results-btn">
              View Results
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              {micSupported && (
                <Button
                  variant={isRecording ? "destructive" : isTranscribing ? "secondary" : "outline"}
                  size="icon"
                  className={`flex-shrink-0 h-10 w-10 rounded-full ${isRecording ? "cop-mic-pulse" : ""}`}
                  onClick={toggleMic}
                  disabled={isTranscribing || sending}
                  data-testid="mic-button"
                  aria-label={isRecording ? "Stop recording" : isTranscribing ? "Transcribing..." : "Start voice input"}
                >
                  {isTranscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
              )}
              <div className="flex-1 relative">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value.substring(0, 500))}
                  onKeyDown={handleKeyDown}
                  placeholder={isRecording ? "Recording..." : isTranscribing ? "Transcribing..." : "Type your answer..."}
                  className="resize-none min-h-[40px] max-h-[120px] pr-2 text-sm"
                  rows={1}
                  disabled={sending}
                  data-testid="message-input"
                />
                <span className="absolute bottom-1 right-2 text-[10px] text-muted-foreground" data-testid="char-counter">
                  {input.length}/500
                </span>
              </div>
              <Button
                size="icon"
                className="flex-shrink-0 h-10 w-10 rounded-full"
                onClick={handleSend}
                disabled={!input.trim() || sending || cooldown}
                data-testid="send-button"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : cooldown ? <span className="text-xs">{cooldownSec}</span> : <Send className="h-4 w-4" />}
              </Button>
            </div>

            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center justify-between mt-1.5" data-testid="recording-indicator">
                <p className="text-xs text-destructive flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                  Recording...
                </p>
                <span className="text-xs font-mono text-destructive tabular-nums" data-testid="mic-timer">
                  {formatTime(recDuration)} / 2:00
                </span>
              </div>
            )}

            {/* Transcribing indicator */}
            {isTranscribing && (
              <p className="text-xs text-primary flex items-center gap-1.5 mt-1.5" data-testid="transcribing-indicator">
                <Loader2 className="h-3 w-3 animate-spin" />
                Transcribing with Whisper...
              </p>
            )}

            <p className="text-[10px] text-muted-foreground mt-1">
              Tap the mic to record, tap again to stop. Whisper AI transcribes maritime terms accurately.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
